// src/store/slices/chitalishtaSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// API Base URL
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://karta.narodnichitalishta.bg/api";

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  list: [],
  towns: [],
  municipalities: [],
  quickSearchResults: [],
  quickSearchLoading: false,
  townsLoading: false,
  municipalitiesLoading: false,
  currentChitalishte: null,
  informationCards: [],
  totalCount: 0,
  loading: false,
  error: null,
  filters: {
    name: "",
    region: "",
    municipality: "",
    town: "",
  },
  sort: {
    sortBy: "name",
    sortDirection: "asc",
  },
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    size: 12,
  },
  isSearchActive: false,
  isHomepageSearchVisible: false,
  mapSelectedChitalishte: null,
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const quickSearchChitalishta = createAsyncThunk(
  "chitalishta/quickSearch",
  async (query, { rejectWithValue }) => {
    if (!query || query.trim().length < 2) {
      return []; // Не търси при по-малко от 2 символа
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/chitalishta/quick-search?query=${encodeURIComponent(
          query
        )}`
      );
      if (!response.ok) {
        throw new Error("Грешка при търсенето");
      }
      const data = await response.json();
      return data.content || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTowns = createAsyncThunk(
  "chitalishta/fetchAllTowns",
  async (_, { rejectWithValue }) => {
    try {
      // console.log("📡 Fetching all unique towns from BE");

      const response = await fetch(`${API_BASE_URL}/chitalishta/towns`);

      if (!response.ok) {
        throw new Error(`Failed to fetch towns: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Fetched towns from BE:", data.length);
      return data;
    } catch (error) {
      console.error("❌ Error fetching towns:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllMunicipalities = createAsyncThunk(
  "chitalishta/fetchAllMunicipalities",
  async (_, { rejectWithValue }) => {
    try {
      // console.log("📡 Fetching all unique municipalities from BE");

      const response = await fetch(`${API_BASE_URL}/chitalishta/municipalities`);

      if (!response.ok) {
        throw new Error(`Failed to fetch municipalities: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Fetched municipalities from BE:", data.length);
      return data;
    } catch (error) {
      console.error("❌ Error fetching municipalities:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch all chitalishta with pagination and sorting
 */
export const fetchAllChitalishta = createAsyncThunk(
  "chitalishta/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 0,
        size = 12,
        sortBy = "name",
        sortDirection = "asc",
      } = params;

      // Построяване на query параметри
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy: sortBy,
        sortDirection: sortDirection,
      });

      // console.log("📡 Fetching all chitalishta with params:", {
      //   page,
      //   size,
      //   sortBy,
      //   sortDirection,
      // });

      const response = await fetch(
        `${API_BASE_URL}/chitalishta?${queryParams}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch chitalishta: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Fetched chitalishta data:", {
      //   contentCount: data.content?.length || 0,
      //   totalElements: data.totalElements,
      //   totalPages: data.totalPages,
      //   currentPage: data.number,
      //   size: data.size,
      // });

      return {
        content: data.content || [],
        totalPages: data.totalPages || 1,
        totalElements: data.totalElements || 0,
        currentPage: data.number || 0,
        size: data.size || size,
        isSearch: false,
      };
    } catch (error) {
      console.error("❌ Error fetching chitalishta:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Search chitalishta with filters
 */
export const searchChitalishta = createAsyncThunk(
  "chitalishta/search",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 0,
        size = 12,
        sortBy = "name",
        sortDirection = "asc",
        name = "",
        region = "",
        municipality = "",
        town = "",
      } = params;

      // Построяване на query параметри за търсене
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy: sortBy,
        sortDirection: sortDirection,
      });

      // Добавяне на филтри само ако има стойност
      if (name && name.trim()) queryParams.append("name", name.trim());
      if (region && region.trim()) queryParams.append("region", region.trim());
      if (municipality && municipality.trim())
        queryParams.append("municipality", municipality.trim());
      if (town && town.trim()) queryParams.append("town", town.trim());

      // console.log("🔍 Searching chitalishta with params:", {
      //   page,
      //   size,
      //   sortBy,
      //   sortDirection,
      //   name,
      //   region,
      //   municipality,
      //   town,
      // });

      const response = await fetch(
        `${API_BASE_URL}/chitalishta/search?${queryParams}`
      );

      if (!response.ok) {
        throw new Error(`Failed to search chitalishta: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Search results:", {
      //   contentCount: data.content?.length || 0,
      //   totalElements: data.totalElements,
      //   totalPages: data.totalPages,
      //   currentPage: data.number,
      //   size: data.size,
      // });

      return {
        content: data.content || [],
        totalPages: data.totalPages || 1,
        totalElements: data.totalElements || 0,
        currentPage: data.number || 0,
        size: data.size || size,
        isSearch: true,
      };
    } catch (error) {
      console.error("❌ Error searching chitalishta:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch single chitalishte by ID
 */
export const fetchChitalishteById = createAsyncThunk(
  "chitalishta/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      // console.log("📡 Fetching chitalishte by ID:", id);

      const response = await fetch(`${API_BASE_URL}/chitalishta/${id}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Читалището не е намерено");
        }
        throw new Error(`Failed to fetch chitalishte: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Loaded chitalishte:", data.name);
      return data;
    } catch (error) {
      console.error("❌ Error fetching chitalishte by ID:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch chitalishte by slug (директно от backend)
 */
export const fetchChitalishteBySlug = createAsyncThunk(
  "chitalishta/fetchBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      // console.log("📡 Fetching chitalishte from backend with slug:", slug);

      const response = await fetch(`${API_BASE_URL}/chitalishta/slug/${slug}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Читалището не е намерено");
        }
        throw new Error("Грешка при зареждане на читалището");
      }

      const data = await response.json();
      // console.log("✅ Loaded chitalishte from backend:", data.name);
      return data;
    } catch (error) {
      console.error("❌ Error fetching chitalishte by slug:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch information cards for a specific chitalishte
 */
export const fetchInformationCardsByChitalishte = createAsyncThunk(
  "chitalishta/fetchInformationCards",
  async (chitalishteId, { rejectWithValue }) => {
    try {
      // console.log(
      //   "📡 Fetching information cards for chitalishte:",
      //   chitalishteId
      // );

      const response = await fetch(
        `${API_BASE_URL}/chitalishta/${chitalishteId}/information-cards`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch information cards: ${response.status}`
        );
      }

      const data = await response.json();
      // console.log("✅ Loaded information cards:", data.length);
      return data;
    } catch (error) {
      console.error("❌ Error fetching information cards:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch total count of all chitalishta
 */
export const fetchTotalCount = createAsyncThunk(
  "chitalishta/fetchTotalCount",
  async (_, { rejectWithValue }) => {
    try {
      // console.log("📡 Fetching total count of chitalishta");

      const response = await fetch(`${API_BASE_URL}/chitalishta/count`);

      if (!response.ok) {
        throw new Error(`Failed to fetch total count: ${response.status}`);
      }

      const data = await response.json();
      // console.log("✅ Total count:", data);
      return data;
    } catch (error) {
      console.error("❌ Error fetching total count:", error);
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// SLICE
// ============================================================================

const chitalishtaSlice = createSlice({
  name: "chitalishta",
  initialState,
  reducers: {
    // Toggle homepage search visibility
    toggleHomepageSearch: (state) => {
      state.isHomepageSearchVisible = !state.isHomepageSearchVisible;
    },

    // Clear quick search results
    clearQuickSearchResults: (state) => {
      state.quickSearchResults = [];
    },

    setMapSelectedChitalishte: (state, action) => {
      state.mapSelectedChitalishte = action.payload;
    },

    // Set filters
    setFilters: (state, action) => {
      // console.log("🎯 Setting filters:", action.payload);
      state.filters = { ...state.filters, ...action.payload };
      // При промяна на филтрите, нулираме пагинацията
      state.pagination.currentPage = 0;
      
      // Проверка дали има активни филтри
      const hasActiveFilters = Object.values(state.filters).some(
        value => value !== null && value !== undefined && value !== ''
      );
      state.isSearchActive = hasActiveFilters;
      
      // console.log("🔍 Search active:", state.isSearchActive);
    },

    // Set sorting
    setSort: (state, action) => {
      // console.log("📊 Setting sort:", action.payload);
      state.sort = { ...state.sort, ...action.payload };
      // При промяна на сортирането, нулираме пагинацията
      state.pagination.currentPage = 0;
    },

    // Set pagination - БЕЗ нулиране на филтри
    setPagination: (state, action) => {
      // console.log("📄 Setting pagination:", action.payload);
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },

    // Clear current chitalishte
    clearCurrentChitalishte: (state) => {
      state.currentChitalishte = null;
      state.informationCards = [];
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear all filters
    clearFilters: (state) => {
      // console.log("🧹 Clearing all filters");
      state.filters = initialState.filters;
      state.pagination.currentPage = 0;
      state.isSearchActive = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========================================
      // Quick Search
      // ========================================
      .addCase(quickSearchChitalishta.pending, (state) => {
        state.quickSearchLoading = true;
      })
      .addCase(quickSearchChitalishta.fulfilled, (state, action) => {
        state.quickSearchLoading = false;
        state.quickSearchResults = action.payload;
      })
      .addCase(quickSearchChitalishta.rejected, (state, action) => {
        state.quickSearchLoading = false;
        state.error = action.payload; // Може да искате отделен error state
      })
      
      // ========================================
      // Fetch All Chitalishta
      // ========================================
      .addCase(fetchAllChitalishta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChitalishta.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.content || [];
        state.totalCount = action.payload.totalElements || 0;
        state.pagination = {
          ...state.pagination,
          totalPages: action.payload.totalPages || 1,
          totalItems: action.payload.totalElements || 0,
          currentPage: action.payload.currentPage || 0,
          size: action.payload.size || state.pagination.size,
        };
        state.isSearchActive = false;
      })
      .addCase(fetchAllChitalishta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.list = [];
      })

      // ========================================
      // Search Chitalishta
      // ========================================
      .addCase(searchChitalishta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchChitalishta.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.content || [];
        state.totalCount = action.payload.totalElements || 0;
        state.pagination = {
          ...state.pagination,
          totalPages: action.payload.totalPages || 1,
          totalItems: action.payload.totalElements || 0,
          currentPage: action.payload.currentPage || 0,
          size: action.payload.size || state.pagination.size,
        };
        state.isSearchActive = true;
        
        // console.log("📊 Updated state after search:", {
        //   totalCount: state.totalCount,
        //   totalPages: state.pagination.totalPages,
        //   currentPage: state.pagination.currentPage,
        //   listLength: state.list.length
        // });
      })
      .addCase(searchChitalishta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.list = [];
      })

      // ========================================
      // Fetch By ID
      // ========================================
      .addCase(fetchChitalishteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChitalishteById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChitalishte = action.payload;
      })
      .addCase(fetchChitalishteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentChitalishte = null;
      })

      // ========================================
      // Fetch By Slug
      // ========================================
      .addCase(fetchChitalishteBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChitalishteBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChitalishte = action.payload;
      })
      .addCase(fetchChitalishteBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentChitalishte = null;
      })

      // ========================================
      // Fetch Information Cards
      // ========================================
      .addCase(fetchInformationCardsByChitalishte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchInformationCardsByChitalishte.fulfilled,
        (state, action) => {
          state.loading = false;
          state.informationCards = action.payload;
        }
      )
      .addCase(fetchInformationCardsByChitalishte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.informationCards = [];
      })

      // ========================================
      // Fetch Total Count
      // ========================================
      .addCase(fetchTotalCount.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTotalCount.fulfilled, (state, action) => {
        state.totalCount = action.payload;
      })
      .addCase(fetchTotalCount.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ========================================
      // Fetch All Towns
      // ========================================
      .addCase(fetchAllTowns.pending, (state) => {
        state.townsLoading = true;
      })
      .addCase(fetchAllTowns.fulfilled, (state, action) => {
        state.townsLoading = false;
        state.towns = action.payload || [];
      })
      .addCase(fetchAllTowns.rejected, (state, action) => {
        state.townsLoading = false;
        state.error = action.payload;
        state.towns = [];
      })

      // ========================================
      // Fetch All Municipalities
      // ========================================
      .addCase(fetchAllMunicipalities.pending, (state) => {
        state.municipalitiesLoading = true;
      })
      .addCase(fetchAllMunicipalities.fulfilled, (state, action) => {
        state.municipalitiesLoading = false;
        state.municipalities = action.payload || [];
      })
      .addCase(fetchAllMunicipalities.rejected, (state, action) => {
        state.municipalitiesLoading = false;
        state.error = action.payload;
        state.municipalities = [];
      });
  },
});

// ============================================================================
// ACTIONS
// ============================================================================

export const {
  setFilters,
  setSort,
  setPagination,
  clearCurrentChitalishte,
  clearError,
  clearFilters,
  toggleHomepageSearch,
  clearQuickSearchResults,
  setMapSelectedChitalishte,
} = chitalishtaSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

export const selectAllChitalishta = (state) => state.chitalishta.list;
export const selectCurrentChitalishte = (state) =>
  state.chitalishta.currentChitalishte;
export const selectInformationCards = (state) =>
  state.chitalishta.informationCards;
export const selectChitalishtaLoading = (state) => state.chitalishta.loading;
export const selectQuickSearchLoading = (state) => state.chitalishta.quickSearchLoading;
export const selectQuickSearchResults = (state) => state.chitalishta.quickSearchResults;
export const selectChitalishtaError = (state) => state.chitalishta.error;
export const selectFilters = (state) => state.chitalishta.filters;
export const selectSort = (state) => state.chitalishta.sort;
export const selectPagination = (state) => state.chitalishta.pagination;
export const selectTotalCount = (state) => state.chitalishta.totalCount;
export const selectAllTowns = (state) => state.chitalishta.towns;
export const selectTownsLoading = (state) => state.chitalishta.townsLoading;
export const selectAllMunicipalities = (state) => state.chitalishta.municipalities;
export const selectMunicipalitiesLoading = (state) => state.chitalishta.municipalitiesLoading;
export const selectItemsPerPage = (state) => state.chitalishta.pagination.size;
export const selectIsSearchActive = (state) => state.chitalishta.isSearchActive;
export const selectIsHomepageSearchVisible = (state) => state.chitalishta.isHomepageSearchVisible;
export const selectMapSelectedChitalishte = (state) => state.chitalishta.mapSelectedChitalishte;

// Complex selectors
export const selectInformationCardsByChitalishteId = (state, chitalishteId) => {
  if (state.chitalishta.currentChitalishte?.id === chitalishteId) {
    return state.chitalishta.informationCards;
  }
  return [];
};

export const selectChitalishteById = (state, id) => {
  return state.chitalishta.list.find((ch) => ch.id === id) || null;
};

export default chitalishtaSlice.reducer;
